// Generic mime-keyed artifact viewer (target-PRD T1.3). Addresses
// artifacts by (deck, version, filename) through the existing preview
// file-serving route — the dead content-hash route is gone. HTML slides
// render in a sandboxed iframe; images inline; text-ish types fetch and
// display; anything else offers download.
import { useEffect, useState, useRef } from 'react';
import { PanelState, containDialogFocus } from '../uikit';

export interface ViewerTarget {
  deckId: string;
  version: string;
  filename: string;
  mime: string;
  previewUrl?: string;
  /** Optional attribution (provenance) shown in the footer. When
   *  deckId/version are present the credit deep-links: clicking swaps
   *  the viewer to the source slide (target-PRD T2.1). */
  source?: { deckTitle: string; slideNum: number; deckId?: string; version?: string } | null;
}

/** Parse a slide-catalog preview_url (…artifact_id=file:artifacts/<v>/<name>)
 *  back into a viewer target. Shared by the library + picker deep-links. */
export function targetFromPreviewUrl(deckId: string, url: string): ViewerTarget | null {
  const m = decodeURIComponent(url).match(/file:artifacts\/([^/]+)\/(.+)$/);
  if (!m) return null;
  return { deckId, version: m[1]!, filename: m[2]!, mime: 'text/html' };
}

/** Canonical slide target — slide files persist as slide-NNN.html. */
export function slideTarget(
  deckId: string,
  version: string,
  slideNum: number,
  source?: ViewerTarget['source'],
): ViewerTarget {
  return {
    deckId,
    version,
    filename: `slide-${String(slideNum).padStart(3, '0')}.html`,
    mime: 'text/html',
    source: source ?? null,
  };
}

export default function ArtifactViewer({
  target,
  onClose,
  onNavigate,
  onReference,
}: {
  target: ViewerTarget;
  onClose: () => void;
  /** Enables the source-credit deep-link: called with the source slide's target. */
  onNavigate?: (t: ViewerTarget) => void;
  /** T1.3 shortcut — stage this slide as a composer reference. */
  onReference?: (() => void) | null;
}) {
  const url = target.previewUrl || `/api/decks/${target.deckId}/preview?artifact_id=${encodeURIComponent(`file:artifacts/${target.version}/${target.filename}`)}`;
  const downloadUrl = /^(data|blob):/.test(url) ? url : `${url}${url.includes('?') ? '&' : '?'}download=1`;
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
      // An artifact refresh can replace its original button while the viewer is open.
      const replacement = document.querySelector<HTMLElement>(`[data-artifact-key="${CSS.escape(`${target.deckId}/${target.version}/${target.filename}`)}"]`);
      (trigger?.isConnected ? trigger : replacement)?.focus();
    };
  }, []);
  const isHtml = target.mime === 'text/html';
  const isImage = target.mime.startsWith('image/');
  const isPdf = target.mime === 'application/pdf';
  const isText = /json|markdown|text\/plain/.test(target.mime);

  useEffect(() => {
    if (!isText) return;
    let cancelled = false;
    setText(null); setFailed(false);
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.text() : Promise.reject(new Error('Read failed')))
      .then(t => { if (!cancelled) setText(t); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [url, isText, retryKey]);

  return (
    <dialog ref={dialogRef} aria-label={target.filename} onKeyDown={containDialogFocus} onCancel={onClose} onClose={onClose} style={{position:'fixed',inset:0,margin:'auto',padding:0,border:'1px solid var(--ds-border)',borderRadius:12,color:'var(--ds-text)',background:'var(--ds-surface)',width:'min(1080px,94vw)',maxHeight:'88dvh'}}>
      <div style={{ background: 'var(--bg-primary)', borderRadius: 12, width: 'min(1080px, 94vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--ds-surface-subtle)' }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {target.filename} <span style={{ opacity: 0.55, fontWeight: 400 }}>· {target.version}</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <a download={target.filename} href={downloadUrl} style={{ fontSize: 12, opacity: 0.7 }}>Download</a>
            <button onClick={onClose} aria-label="Close viewer" style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', background: isHtml ? 'var(--ds-canvas)' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          {isHtml && (
            <iframe src={url} title={target.filename} sandbox="" style={{ width: 'min(960px, 100%)', aspectRatio: '16/9', border: 'none', background: 'var(--ds-surface)', boxShadow: '0 2px 16px var(--ds-shadow-color)' }} />
          )}
          {isImage && <img src={url} alt={target.filename} style={{ maxWidth: '100%', maxHeight: '78vh' }} />}
          {isPdf && <object data={url} type="application/pdf" style={{ width: '100%', height: '78vh' }}>PDF preview unavailable — use Download.</object>}
          {isText && failed && <PanelState kind="error" title="Couldn't load this file" description="Try opening the saved file again." action="Retry file" onAction={() => setRetryKey(k => k + 1)} />}
          {isText && !failed && <pre style={{ alignSelf: 'stretch', margin: 0, padding: 16, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text ?? 'Loading…'}</pre>}
          {!isHtml && !isImage && !isPdf && !isText && (
            <div style={{ opacity: 0.6, padding: 30 }}>No inline preview for {target.mime} — use Download.</div>
          )}
        </div>
        {(target.source || onReference) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 16px', borderTop: '1px solid var(--ds-surface-subtle)', fontSize: 12 }}>
            <div style={{ opacity: 0.75, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {target.source && (target.source.deckId && target.source.version && onNavigate ? (
                <button
                  onClick={() => onNavigate(slideTarget(target.source!.deckId!, target.source!.version!, target.source!.slideNum))}
                  title="Open the source slide"
                  style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--ds-accent)', cursor: 'pointer' }}
                >
                  Source: {target.source.deckTitle} · slide {target.source.slideNum} ↗
                </button>
              ) : (
                <>Source: {target.source.deckTitle} · slide {target.source.slideNum}</>
              ))}
            </div>
            {onReference && (
              <button
                onClick={onReference}
                title="Add this slide to the composer's pending references"
                style={{ flexShrink: 0, fontSize: 12, padding: '4px 12px', borderRadius: 8, border: '1px solid var(--ds-accent-soft)', background: 'var(--ds-accent-soft)', color: 'var(--ds-accent)', cursor: 'pointer' }}
              >
                ↩ Reference this slide
              </button>
            )}
          </div>
        )}
      </div>
    </dialog>
  );
}
